import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import Whats from "../../assets/images/icons/Ic_WhatsApp.svg";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import i18n from "i18next";
import { useGetTimeToResendCodeChangeNumber } from "../../api/changePhoneNumber/useGetTimeToResendCodeChangeNumber";
import useVerifyCodeChangeNumber, {
  VerifyCodeChangeNumberRequest,
} from "../../api/changePhoneNumber/useVerifyCodeChangeNumber";
import SMS from "../../assets/images/icons/SMS_IC.svg";
import { ConfirmationPopup } from "../../components";
import { resetUserDetails } from "../../redux/reducers/registrationSlice";
import { resetUserState } from "../../redux/reducers/userSlice";
import { persistor } from "../../redux/store";
import { useToast } from "../../Services/ToastContext";
import AuthLayout from "../../Layout/AuthLayout";
import { EndPoints } from "../../Constants/endpoints";
import { useAppSelector, useMaskedPhoneNumber, usePostData } from "../../hooks";
import { MessageBtn } from "../../Share";

interface VerifyPhoneNumberInputs {
  code: string[];
}

const VerifyNewPhoneNumber: React.FC = () => {
  const newPhoneNumber = useAppSelector(
    (state) => state.registration.newPhoneNumber,
  );
  const ChangePhoneNumberId = useAppSelector(
    (state) => state.registration.ChangePhoneNumberId,
  );
  const CountryId = useAppSelector(
    (state) => state.registration.userDetails.CountryId,
  );

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useToast();

  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (isNaN(ChangePhoneNumberId)) {
      showToast(t("invalid_user_id"), "error");
      navigate("/login", { replace: true });
    }
  }, [ChangePhoneNumberId, navigate, showToast, t]);

  const { data: resendData, refetch: refetchResendData } =
    useGetTimeToResendCodeChangeNumber(ChangePhoneNumberId);

  const { mutate: verifyMutate } = useVerifyCodeChangeNumber();

  const { mutate: resendMutate, isPending: resendIsPending } = usePostData(
    EndPoints.resendVerificationCodeChangeNumber,
  );

  const [resendCodeCount, setResendCodeCount] = useState<number>(0);
  const [resendDate, setResendDate] = useState<number | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const maxResendAttempts = 4;
  const [showResendOptions, setShowResendOptions] = useState(false);

  const queryClient = useQueryClient();

  // Update resendCodeCount and resendDate when data is fetched
  useEffect(() => {
    if (resendData && resendData.Status && resendData.Data) {
      setResendCodeCount(resendData.Data.ResendCodeCount);
      setResendDate(parseFloat(resendData.Data.ResendDate));
      const newTimeLeft = calculateTimeLeft(
        resendData.Data.ResendCodeCount,
        parseFloat(resendData.Data.ResendDate),
      );
      setTimeLeft(newTimeLeft);
    }
  }, [resendData]);

  // Handle Timer Countdown
  useEffect(() => {
    if (timeLeft > 0) {
      const timeout = setTimeout(() => {
        setTimeLeft((prevTime) => Math.max(prevTime - 1, 0));
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [timeLeft]);

  // Calculate Time Left based on Resend Count and Resend Date
  const calculateTimeLeft = (
    resendCodeCount: number,
    resendDate: number,
  ): number => {
    const now = Date.now() / 1000; // current time in seconds
    const elapsed = now - resendDate;
    let waitTime = 0;

    switch (resendCodeCount) {
      case 1:
        waitTime = 2 * 60; // 2 minutes
        break;
      case 2:
        waitTime = 5 * 60; // 5 minutes
        break;
      case 3:
        waitTime = 30 * 60; // 30 minutes
        break;
      default:
        waitTime = 0;
        break;
    }

    let remainingTime = waitTime - elapsed;
    // Ensure remainingTime does not exceed the waitTime
    if (resendCodeCount === 1 && remainingTime > 120) {
      remainingTime = 120;
    }
    if (resendCodeCount === 2 && remainingTime > 300) {
      remainingTime = 300;
    }
    if (resendCodeCount === 3 && remainingTime > 1800) {
      remainingTime = 1800;
    }

    return remainingTime > 0 ? Math.floor(remainingTime) : 0;
  };

  // Format Time for Display
  const formatTime = (seconds: number) => {
    const totalSeconds = Math.floor(seconds);
    const m = Math.floor(totalSeconds / 60).toString();
    const s = (totalSeconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  useEffect(() => {
    if (!ChangePhoneNumberId) {
      navigate("/login", { replace: true });
    }
  }, [ChangePhoneNumberId, navigate]);

  const methods = useForm<VerifyPhoneNumberInputs>({
    defaultValues: { code: ["", "", "", "", "", ""] },
  });
  const { handleSubmit, control, setValue, watch, reset } = methods;
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const watchFields = watch("code");
  const isComplete = watchFields.every((code) => code !== "");

  // ---- Added this effect to auto-verify when code is complete ----
  useEffect(() => {
    if (isComplete) {
      handleSubmit(onSubmit)();
    }
  }, [isComplete, handleSubmit]);

  // Handle Resend Code
  const handleResendCode = () => {
    setFormError(null);
    if (resendCodeCount >= maxResendAttempts) {
      setFormError(t("user_not_able_to_send_more_codes_until_24_hours"));
      return;
    }
    if (timeLeft > 0) {
      return;
    }
    setShowResendOptions(true);
  };

  const handleSendCode = (via: "sms" | "whatsapp") => {
    setFormError(null);
    if (!ChangePhoneNumberId) {
      showToast(t("user_id_missing"), "error");
      navigate("/login", { replace: true });
      return;
    }

    resendMutate(
      {
        Id: ChangePhoneNumberId,
        Channel: via === "whatsapp" ? "WhatsApp" : "SMS",
      },
      {
        onSuccess: (data) => {
          // Invalidate and refetch the resend time data
          queryClient.invalidateQueries({
            queryKey: ["GetTimeToResendCodeChangeNumber", ChangePhoneNumberId],
          });
          refetchResendData();

          resetFields();
          showToast(t("code_resent_successfully"), "success");
          setShowResendOptions(false);

          // Manually set timeLeft based on the new resendCodeCount
          const newResendCodeCount = resendCodeCount + 1;
          let newWaitTime = 0;
          switch (newResendCodeCount) {
            case 1:
              newWaitTime = 2 * 60;
              break;
            case 2:
              newWaitTime = 5 * 60;
              break;
            case 3:
              newWaitTime = 30 * 60;
              break;
            default:
              newWaitTime = 0;
              break;
          }
          setTimeLeft(newWaitTime);
        },
        onError: (error: any) => {
          const errorData = error.response?.data;
          setFormError(
            errorData?.ErrorMessage ||
              error.message ||
              t("an_error_occurred_please_try_again"),
          );
        },
      },
    );
  };

  const handleSendCodeViaSMS = () => handleSendCode("sms");
  const handleSendCodeViaWhatsApp = () => handleSendCode("whatsapp");

  const onSubmit: SubmitHandler<VerifyPhoneNumberInputs> = (data) => {
    if (!isComplete) {
      setFormError(t("enter_verification_code"));
      return;
    }
    if (!ChangePhoneNumberId) {
      setFormError(t("user_id_missing"));
      navigate("/login", { replace: true });
      return;
    }

    const requestData: VerifyCodeChangeNumberRequest = {
      Id: ChangePhoneNumberId,
      VerificationCode: data.code.join(""),
      CountryId: CountryId,
      PhoneNumber: newPhoneNumber,
    };

    verifyMutate(requestData, {
      onSuccess: async () => {
        // Reset Redux Store
        dispatch(resetUserDetails());
        dispatch(resetUserState());

        // Purge Persisted Store
        try {
          await persistor.purge();
        } catch (error) {}

        // Redirect to Login
        navigate("/login", { replace: true });
      },
      onError: (error: any) => {
        const errorData = error.response?.data;
        setFormError(
          errorData?.ErrorMessage ||
            error.message ||
            t("invalid_code_try_again"),
        );
        resetFields();
      },
    });
  };

  const maskedPhoneNumber = useMaskedPhoneNumber(newPhoneNumber);

  const handleChange = (value: string, index: number) => {
    if (/^\d?$/.test(value)) {
      setValue(`code.${index}`, value);
      if (value.length === 1 && index < 5) {
        inputsRef.current[index + 1]?.focus();
      } else if (value === "" && index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
    }
  };

  const resetFields = () => {
    for (let i = 0; i < 6; i++) {
      setValue(`code.${i}`, "");
    }
    inputsRef.current[0]?.focus();
  };

  // Prevent page unload
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Handle browser back navigation
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();
      setIsModalOpen(true);
    };
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const handleConfirmGoBack = () => {
    setIsModalOpen(false);
    navigate("/");
  };

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  // Handle Paste Event
  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasteData = event.clipboardData.getData("Text");
    if (/^\d{6}$/.test(pasteData)) {
      for (let i = 0; i < 6; i++) {
        setValue(`code.${i}`, pasteData.charAt(i));
      }
      inputsRef.current[5]?.focus();
    }
  };

  useEffect(() => {
    setFormError(null);
  }, [i18n.language]);

  return (
    <FormProvider {...methods}>
      <AuthLayout
        title={t("verify_your_phone_number")}
        description={
          <>
            {t("we_send_code_to")}
            <span dir="ltr"> {maskedPhoneNumber} </span>
          </>
        }
        lottieImage={null}
      >
        {/* Removed type='submit' from form to avoid automatic submission on Enter */}
        <form className="w-full" onSubmit={(e) => e.preventDefault()}>
          <div dir="ltr" className="mb-8 flex justify-between">
            {[...Array(6)].map((_, index) => (
              <Controller
                key={index}
                name={`code.${index}` as const}
                control={control}
                render={({ field }) => (
                  <input
                    className={`h-[50px] w-[75px] rounded-md border border-systemBg text-center text-2xl focus:outline-primary ${
                      formError
                        ? "border-2 border-solid border-error"
                        : field.value
                          ? "border-primary"
                          : "focus:outline-primary"
                    } ${index === 2 ? "me-8" : "me-2"}`}
                    maxLength={1}
                    {...field}
                    value={field.value || ""}
                    onChange={(e) => handleChange(e.target.value, index)}
                    ref={(el) => (inputsRef.current[index] = el)}
                    aria-label={t("verification_code_digit", {
                      digit: index + 1,
                    })}
                    onPaste={index === 0 ? handlePaste : undefined}
                  />
                )}
              />
            ))}
          </div>

          {formError && (
            <p className="mb-4 text-center text-error" aria-live="assertive">
              {formError}
            </p>
          )}

          <div className="mb-4 flex items-center justify-center gap-4">
            {resendCodeCount !== null && !showResendOptions && (
              <button
                type="button"
                className={`${
                  timeLeft > 0 || resendCodeCount >= maxResendAttempts
                    ? "text-body-text cursor-not-allowed underline underline-offset-4"
                    : "cursor-pointer font-bold text-primary underline underline-offset-4"
                }`}
                onClick={handleResendCode}
                disabled={
                  timeLeft > 0 ||
                  resendCodeCount >= maxResendAttempts ||
                  resendIsPending
                }
              >
                {t("send_code_again")}
              </button>
            )}
            {timeLeft > 0 && (
              <p className="text-body-text mt-1">{formatTime(timeLeft)}</p>
            )}
          </div>

          {showResendOptions && (
            <div className="my-4 flex flex-col items-center gap-4">
              <MessageBtn
                icon={Whats}
                title={t("send_code_via_whatsapp")}
                onClick={handleSendCodeViaWhatsApp}
                disabled={resendIsPending}
              />
              <MessageBtn
                icon={SMS}
                title={t("send_code_via_sms")}
                onClick={handleSendCodeViaSMS}
                disabled={resendIsPending}
                color="blue"
              />
            </div>
          )}

          {resendCodeCount >= maxResendAttempts && (
            <p className="mb-4 text-center text-error">
              {t("user_not_able_to_send_more_codes_until_24_hours")}
            </p>
          )}
        </form>
      </AuthLayout>

      {isModalOpen && (
        <ConfirmationPopup
          isModalOpen={isModalOpen}
          title={t("all_info_will_lost")}
          description={t("are_you_sure_to_do_action")}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleConfirmGoBack}
        />
      )}
    </FormProvider>
  );
};

export default VerifyNewPhoneNumber;
